import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const DIGIT = 'N';
const PLACEHOLDER = '_';

/**
 * Digit mask for text inputs, such as `literaxMask="NN NNNN NNNN"`: each `N` takes a digit and
 * every other character is shown as is. Replaces `yopsilon-mask` (feature 024, T072), whose last
 * release is compiled in View Engine, and keeps the value it gave the form: while typing, the
 * text shown with `_` for the missing digits; once complete, the masked number; after leaving the
 * field with an incomplete number, an empty string. Letters and extra digits are ignored, and a
 * pasted number keeps only its digits.
 */
@Directive({
  selector: 'input[literaxMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputMaskDirective),
      multi: true,
    },
  ],
})
export class InputMaskDirective implements ControlValueAccessor {
  @Input('literaxMask') mask = '';

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private element: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  private get input(): HTMLInputElement {
    return this.element.nativeElement;
  }

  private get size(): number {
    return this.mask.split(DIGIT).length - 1;
  }

  writeValue(value: string | null): void {
    // Shown as given, like yopsilon-mask: the forms already set it masked.
    this.renderer.setProperty(this.input, 'value', value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.input, 'disabled', isDisabled);
  }

  @HostListener('input')
  onInput(): void {
    const value = this.input.value;
    const caret = this.input.selectionStart ?? value.length;
    const digits = this.digitsOf(value);
    this.update(digits, Math.min(this.digitsOf(value.slice(0, caret)).length, digits.length));
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const { selectionStart: start, selectionEnd: end } = this.input;
    if ((event.key !== 'Backspace' && event.key !== 'Delete') || start === null || start !== end) {
      return;
    }
    // Deleting by hand so the caret skips the mask characters and placeholders.
    event.preventDefault();
    const digits = this.digitsOf(this.input.value);
    const before = this.digitsOf(this.input.value.slice(0, start)).length;
    const index = event.key === 'Backspace' ? before - 1 : before;
    if (index < 0 || index >= digits.length) {
      return;
    }
    this.update(digits.slice(0, index) + digits.slice(index + 1), index);
  }

  @HostListener('blur')
  onBlur(): void {
    const digits = this.digitsOf(this.input.value);
    if (digits.length > 0 && digits.length < this.size) {
      this.update('', 0);
    }
    this.onTouched();
  }

  private digitsOf(text: string): string {
    return text.replace(/\D/g, '').slice(0, this.size);
  }

  /** Shows the digits through the mask, sends the text to the form and puts the caret on the slot of digit `caretDigits`. */
  private update(digits: string, caretDigits: number): void {
    let text = '';
    let caret = 0;
    if (digits.length > 0) {
      let used = 0;
      for (const char of this.mask) {
        if (char === DIGIT) {
          if (used === caretDigits) {
            caret = text.length;
          }
          text += digits[used] ?? PLACEHOLDER;
          used++;
        } else {
          text += char;
        }
      }
      if (caretDigits >= this.size) {
        caret = text.length;
      }
    }
    this.renderer.setProperty(this.input, 'value', text);
    this.input.setSelectionRange(caret, caret);
    this.onChange(text);
  }
}
